import { Button } from "src/components/ui";
import React, { FC, Suspense, useEffect, useRef, useState } from 'react';
import { ErrorBoundary } from '@sentry/react';
import InlineLoading from 'src/components/common/InlineLoading/InlineLoading';
import { useHelpDrawer } from '../../../contexts/HelpDrawerContext';
import { FontAwesomeIcon, FontAwesomeIcons } from '../../../constants/fontAwesomeIcons';

import './HelpDrawer.scss';

const MIN_WIDTH = 500;
const STORAGE_KEY = 'helpDrawerWidth';
const HelpDrawerContent = React.lazy(() => import('./HelpDrawerContent'));

const getMaxWidth = () => {
    if (window.innerWidth < 992) {
        return Math.max(0, Math.min(window.innerWidth * 0.88, window.innerWidth - 24));
    }
    return Math.max(0, Math.min(window.innerWidth * 0.4, window.innerWidth - 384));
};

const clampWidth = (width: number, maxWidth = getMaxWidth()) =>
    Math.max(0, Math.min(Number.isFinite(width) ? width : MIN_WIDTH, maxWidth));

const HelpDrawer: FC = () => {
    const { isOpen, hasOpened, setIsOpen } = useHelpDrawer();

    const stopResize = useRef<(() => void) | null>(null);
    const [width, setWidth] = useState<number>(() => {
        const savedWidth = localStorage.getItem(STORAGE_KEY);
        const parsedWidth = savedWidth ? parseInt(savedWidth, 10) : MIN_WIDTH;
        return clampWidth(Number.isFinite(parsedWidth) && parsedWidth > 0 ? parsedWidth : MIN_WIDTH);
    });
    const [maxWidth, setMaxWidth] = useState(getMaxWidth);
    const maxWidthRef = useRef<number>(maxWidth);
    const visibleWidth = clampWidth(width, maxWidth);

    useEffect(() => {
        const handleResize = () => {
            const nextMaxWidth = getMaxWidth();
            maxWidthRef.current = nextMaxWidth;
            setMaxWidth(nextMaxWidth);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const rippleEffect = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        const ripple = document.createElement("span");
        ripple.classList.add("ripple");
        event.currentTarget.appendChild(ripple);
        setTimeout(() => ripple.remove(), 300);
    };

    const handleOnClickEvent: React.MouseEventHandler<HTMLButtonElement> = (event) => {
        rippleEffect(event);
        setIsOpen(!isOpen);
    };

    const handleMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
        stopResize.current?.();
        const drag = { startX: event.clientX, startWidth: visibleWidth };
        const style = document.body.style;
        const previousUserSelect = style.getPropertyValue('user-select');
        const previousCursor = style.getPropertyValue('cursor');
        const previousUserSelectPriority = style.getPropertyPriority('user-select');
        const previousCursorPriority = style.getPropertyPriority('cursor');
        style.setProperty('user-select', 'none');
        style.setProperty('cursor', 'ew-resize');
        let dragWidth = drag.startWidth;
        const handleMouseMove = (event: MouseEvent) => {
            const dx = drag.startX - event.clientX;
            dragWidth = clampWidth(Math.max(drag.startWidth + dx, Math.min(MIN_WIDTH, maxWidthRef.current)), maxWidthRef.current);
            setWidth(dragWidth);
        };
        const handleMouseUp = () => {
            localStorage.setItem(STORAGE_KEY, String(dragWidth));
            stopResize.current?.();
        };
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
        stopResize.current = () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            style.setProperty('user-select', previousUserSelect, previousUserSelectPriority);
            style.setProperty('cursor', previousCursor, previousCursorPriority);
            stopResize.current = null;
        };
    };

    useEffect(() => {
        if (!isOpen) {
            stopResize.current?.();
        }
        return () => stopResize.current?.();
    }, [isOpen]);

    return (
        <div
            id="help-drawer"
            className="help-drawer-container"
            data-open={isOpen}
            style={{
                width: isOpen ? `${visibleWidth}px` : '0px',
            }}
        >
            {isOpen && <button className="help-drawer-backdrop" aria-label="Dismiss help overlay" type="button" onClick={() => setIsOpen(false)} />}
            <div
                className="help-drawer-fixed-container"
                data-open={isOpen}
                style={{
                    width: isOpen ? `${visibleWidth}px` : '0px',
                }}
            >
                <div style={{ height: '65px' }}></div>

                <div className='container'>
                    <div className='row pt-2'>
                        <div className='col-auto me-auto'>
                            <h1>Help</h1>
                        </div>
                        <div className='col-auto'>
                            <button
                                className="close-button close-button-transition"
                                onClick={handleOnClickEvent}
                                type="button"
                                aria-label="Close help"
                            >
                                <FontAwesomeIcon
                                    className="close-button-icon"
                                    icon={FontAwesomeIcons.Regular.TIMES}
                                    fixedWidth
                                />
                            </button>
                        </div>
                    </div>
                </div>

                <div className='help-topic-container custom-scrollbar'>
                    {(isOpen || hasOpened) && (
                        <ErrorBoundary fallback={
                            <div role="alert">
                                <p>Unable to load Help. Reload the page to try again.</p>
                                <Button type="button" variant="secondary" onClick={() => window.location.reload()}>
                                    Reload page
                                </Button>
                            </div>
                        }>
                            <Suspense fallback={<div role="status" aria-label="Loading help"><InlineLoading /></div>}>
                                <HelpDrawerContent />
                            </Suspense>
                        </ErrorBoundary>
                    )}
                </div>

                {isOpen && <div className="resize-handle" onMouseDown={handleMouseDown} />}
            </div>
        </div>
    );
};

export default HelpDrawer;
